/*
 * WhatsApp floating control - clearance from clickable content.
 *
 * The control is position: fixed in a bottom corner, so whatever happens to sit
 * in that corner at a given scroll position ends up underneath it. Most of those
 * cases are already handled declaratively in pv-whatsapp-safe.css: an open cart
 * drawer, a modal, the consent banner, the Shop the Look panel, and the sticky
 * purchase bars, which the layout lifts the control above.
 *
 * Two cases are not reachable from CSS, because they are questions about
 * geometry rather than about state:
 *
 *   - the homepage hero CTA at desktop widths. Measured at 1440x900 on the live
 *     theme: the control occupies x 1349-1409 and "Explore Collection" occupies
 *     x 1182-1375, so 26px of the button sits under the control. That is already
 *     true in English today. Arabic only escaped it because the control did not
 *     mirror, and mirroring it correctly reproduced the same 26px overlap on the
 *     other side.
 *
 *   - the inline Buy Now / Add to Cart / Chat With Us row on a product or
 *     package page at phone widths, which is on screen for the one scroll
 *     position before the sticky bar takes over.
 *
 * Rather than name those elements - a list that goes stale the moment a section
 * is edited - this measures. It asks the only question that matters: is any
 * clickable thing actually underneath the control right now. If so the control
 * steps aside, exactly the way it already does for every other blocking surface,
 * and it comes straight back. It is never hidden for longer than something is
 * genuinely under it, and it never moves, so nothing reflows.
 *
 * Hysteresis is asymmetric on purpose: it engages on any overlap, and only
 * disengages once there is CLEAR_PX of daylight. Without that, an edge sitting
 * exactly on the boundary would toggle on every scroll frame.
 */
(function () {
  'use strict';

  var control = document.getElementById('whatsapp-sticky-icon');
  if (!control) return;

  var BLOCKED_CLASS = 'pv-wa-blocked';
  var CLEAR_PX = 8; // daylight required before the control comes back
  var MIN_W = 24; // ignore hairline and decorative anchors
  var MIN_H = 16;
  /*
   * Only CONTROL-shaped things count, which is the difference between stepping
   * aside and hiding.
   *
   * A floating control necessarily sits over page content, and on a grid or a
   * recommendation carousel the thing underneath it is a whole product card
   * wrapped in one <a>. Covering 60px of a 300x420 card is under 3% of it and
   * the card stays clickable everywhere else - treating that as a collision
   * would hide the control on most of the site, which is just hiding it.
   *
   * Covering 26px of a 193x43 button is a different thing entirely: part of a
   * discrete target becomes unclickable. Height separates the two cleanly -
   * buttons and icon links measured here are 36-45px tall, product cards are
   * 400px and up - with a wide margin either side of the cut.
   */
  var MAX_CONTROL_H = 96;
  var CANDIDATE_SELECTOR = 'a[href],button,[role="button"],input[type="submit"],input[type="button"],summary';

  var candidates = [];
  var blocked = false;
  var queued = false;

  function collect() {
    var found = document.querySelectorAll(CANDIDATE_SELECTOR);
    var list = [];
    for (var i = 0; i < found.length; i++) {
      var el = found[i];
      if (el === control || control.contains(el) || el.contains(control)) continue;
      list.push(el);
    }
    candidates = list;
  }

  function overlaps() {
    // The control is fixed, so its rect is viewport-relative and cheap to read.
    var c = control.getBoundingClientRect();
    if (!c.width || !c.height) return false;

    // When already stepped aside, require daylight before coming back.
    var pad = blocked ? CLEAR_PX : 0;
    var top = c.top - pad;
    var bottom = c.bottom + pad;
    var left = c.left - pad;
    var right = c.right + pad;

    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (!el.isConnected) continue;
      var b = el.getBoundingClientRect();
      if (b.width < MIN_W || b.height < MIN_H) continue;
      if (b.height > MAX_CONTROL_H) continue; // a content block, not a control
      if (b.bottom < top || b.top > bottom || b.right < left || b.left > right) continue;
      // Something with a real hit area is under the control.
      var cs = window.getComputedStyle(el);
      if (cs.pointerEvents === 'none' || cs.visibility === 'hidden' || cs.display === 'none') continue;
      return true;
    }
    return false;
  }

  function measure() {
    queued = false;
    var next = overlaps();
    if (next === blocked) return;
    blocked = next;
    document.body.classList.toggle(BLOCKED_CLASS, blocked);
  }

  function schedule() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(measure);
  }

  /*
   * A trailing re-check, because content moves after scrolling stops.
   *
   * Sections in this theme reveal on scroll with a transform, and lazy images
   * resolve their intrinsic size late. Both settle AFTER the last scroll event,
   * so a scroll-only listener measures the pre-reveal position and never looks
   * again. Two product-page positions were caught this way during testing -
   * "Book Free Consultation" at 375px and the Facebook link at 768px - where
   * the element slid under the control once its reveal finished.
   */
  var settle = null;
  function scheduleSettled() {
    schedule();
    clearTimeout(settle);
    settle = setTimeout(schedule, 400);
  }

  collect();
  schedule();

  window.addEventListener('scroll', scheduleSettled, { passive: true });
  window.addEventListener('resize', scheduleSettled, { passive: true });
  window.addEventListener('orientationchange', scheduleSettled, { passive: true });
  // Reveals, accordions and late-sizing images all end in one of these. rAF
  // coalescing means a noisy event is still at most one measurement per frame.
  document.addEventListener('transitionend', schedule, true);
  document.addEventListener('animationend', schedule, true);
  window.addEventListener('load', scheduleSettled);
  // The sticky purchase bars and the cart drawer change what is on screen
  // without a scroll, and sections stream in after first paint.
  if (typeof MutationObserver === 'function') {
    var debounce = null;
    new MutationObserver(function () {
      clearTimeout(debounce);
      debounce = setTimeout(function () {
        collect();
        schedule();
      }, 250);
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'class', 'style'] });
  }
})();
